import React, { useEffect, useMemo, useState } from 'react';
import CalendarFC from './CalendarFC';
import EventoForm from './EventoForm';
import CursadaForm from './CursadaForm';
import { useAgenda } from '../state';
import { detectConflicts as detectConflictsStatic, selectEventosUnicos, expandCursadasEnRango } from '../state';
import '../styles/agenda.css';
import { parseICS } from '../utils/ics';

export default function AgendaPage() {
  const { state, addEvento, addCursada, updateCursada, removeCursada, removeEvento, selectCalendarEvents, detectConflicts, clearAll } = useAgenda();
  const [showCursadaModal, setShowCursadaModal] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [view, setView] = useState(() => {
    try { return JSON.parse(localStorage.getItem('agenda@prefs')||'{}').view || 'dayGridMonth'; } catch { return 'dayGridMonth'; }
  });
  const [initialDate, setInitialDate] = useState(() => {
    try { const saved = JSON.parse(localStorage.getItem('agenda@prefs')||'{}').dateISO; if (saved) return saved; } catch {}
    const d = new Date(); // usuario local
    d.setDate(1); return d.toISOString();
  });

  const events = useMemo(() => {
    const list = selectCalendarEvents();
    const conflicts = detectConflicts();
    return list.map((ev) => ({
      ...ev,
      classNames: conflicts.has(ev.id) ? ['event-conflict'] : [],
    }));
  }, [state.eventos, state.cursadas, selectCalendarEvents, detectConflicts]);

  // Demo cursada Lu/Vi 18:30–20:00 para verificar semana 11–17 ago 2025
  useEffect(() => {
    try {
      const DEMO_KEY = 'agenda_demo_v1';
      if (!localStorage.getItem(DEMO_KEY)) {
        addCursada({
          id: crypto.randomUUID?.() || String(Date.now()),
          termId: state.activeTermId || null,
          materia: 'Sociología',
          dias: [1, 5], // Lu y Vi
          horaInicio: '18:30',
          horaFin: '20:00',
          desde: '2025-08-01',
          hasta: '2025-08-31',
          visible: true,
        });
        localStorage.setItem(DEMO_KEY, '1');
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleAddEvento(data) {
    addEvento({
      id: data.id,
      termId: state.activeTermId || null,
      tipo: data.tipo,
      titulo: data.materia || data.tipo,
      fecha: data.start.slice(0, 10),
      horaInicio: data.start.slice(11, 16),
      horaFin: data.end.slice(11, 16),
      color: undefined,
    });
  }

  function handleAddCursada(data) {
    addCursada({
      id: data.id,
      termId: state.activeTermId || null,
      materia: data.materia,
      dias: data.dias.map((d) => ({ lu: 1, ma: 2, mi: 3, ju: 4, vi: 5, sa: 6 }[d])),
      horaInicio: data.horaInicio,
      horaFin: data.horaFin,
      desde: data.desde,
      hasta: data.hasta,
      aula: data.aula,
      color: data.color,
    });
  }

  function exportCSV() {
    const rows = [['tipo', 'materia', 'fecha_inicio', 'fecha_fin', 'dias', 'hora_inicio', 'hora_fin', 'descripcion']];
    for (const e of state.eventos) {
      rows.push([e.tipo, e.titulo, `${e.fecha} ${e.horaInicio || ''}`, `${e.fecha} ${e.horaFin || ''}`, '', e.horaInicio || '', e.horaFin || '', e.notas || '']);
    }
    for (const c of state.cursadas) {
      rows.push(['Cursada', c.materia, c.desde, c.hasta, (c.dias || []).join('/'), c.horaInicio, c.horaFin, c.aula || '']);
    }
    const csv = rows.map((r) => r.map((x) => `"${String(x ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'agenda.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  function exportICS() {
    const lines = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Mapa Abogacia UBA//Agenda//ES'];
    // Eventos únicos
    for (const e of state.eventos) {
      const dtStart = e.fecha + 'T' + (e.horaInicio || '00:00') + '00';
      const dtEnd = e.fecha + 'T' + (e.horaFin || e.horaInicio || '00:00') + '00';
      lines.push('BEGIN:VEVENT');
      lines.push('UID:' + e.id);
      lines.push('DTSTART:' + dtStart.replace(/[-:]/g, ''));
      lines.push('DTEND:' + dtEnd.replace(/[-:]/g, ''));
      lines.push('SUMMARY:' + (e.tipo + ': ' + e.titulo));
      lines.push('END:VEVENT');
    }
    // Cursadas como RRULE semanal
    for (const c of state.cursadas) {
      const diasNum = (c.dias || []).map((d) => (typeof d === 'string' ? { lu: 1, ma: 2, mi: 3, ju: 4, vi: 5, sa: 6 }[d] : d));
      const byday = diasNum.map((n) => ['MO', 'TU', 'WE', 'TH', 'FR', 'SA'][n - 1]).join(',');
      const dtStart = c.desde.replace(/-/g, '') + 'T' + c.horaInicio.replace(':', '') + '00';
      const dtEnd = c.desde.replace(/-/g, '') + 'T' + c.horaFin.replace(':', '') + '00';
      const until = c.hasta.replace(/-/g, '') + 'T235959';
      lines.push('BEGIN:VEVENT');
      lines.push('UID:' + c.id);
      lines.push('DTSTART:' + dtStart);
      lines.push('DTEND:' + dtEnd);
      lines.push('RRULE:FREQ=WEEKLY;BYDAY=' + byday + ';UNTIL=' + until);
      lines.push('SUMMARY:Cursada: ' + c.materia);
      lines.push('END:VEVENT');
    }
    lines.push('END:VCALENDAR');
    const ics = lines.join('\n');
    const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'agenda.ics';
    a.click();
    URL.revokeObjectURL(url);
  }

  function importCSV(file) {
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || '');
      const [headerLine, ...lines] = text.split(/\r?\n/).filter(Boolean);
      const headers = headerLine.split(',').map((h) => h.replace(/"/g, '').replace(/"/g, '').trim());
      const idx = Object.fromEntries(headers.map((h, i) => [h, i]));
      for (const line of lines) {
        const cols = line.match(/"([^"]|"")*"|[^,]+/g) || [];
        const pick = (k) => (cols[idx[k]] || '').replace(/^"|"$/g, '').replace(/""/g, '"');
        const tipo = pick('tipo');
        if (tipo === 'Cursada') {
          addCursada({
            id: crypto.randomUUID?.() || String(Date.now()),
            termId: state.activeTermId || null,
            materia: pick('materia'),
            dias: (pick('dias') || '').split('/').filter(Boolean).map((x) => x.trim()),
            horaInicio: pick('hora_inicio') || '00:00',
            horaFin: pick('hora_fin') || '00:00',
            desde: (pick('fecha_inicio') || '').slice(0, 10),
            hasta: (pick('fecha_fin') || '').slice(0, 10),
          });
        } else {
          const fechaInicio = (pick('fecha_inicio') || '').split(' ')[0];
          const horaInicio = (pick('fecha_inicio') || '').split(' ')[1] || '';
          const horaFin = (pick('fecha_fin') || '').split(' ')[1] || '';
          addEvento({
            id: crypto.randomUUID?.() || String(Date.now()),
            termId: state.activeTermId || null,
            tipo: tipo || 'Otro',
            titulo: pick('materia') || tipo || 'Evento',
            fecha: fechaInicio,
            horaInicio: horaInicio,
            horaFin: horaFin,
          });
        }
      }
    };
    reader.readAsText(file, 'utf-8');
  }

  function importICS(file) {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = String(reader.result || '');
        const parsed = parseICS(text);
        let createdEventos = 0;
        let createdCursadas = 0;
        const beforeConflicts = detectConflictsStatic({ ...state }, state.activeTermId);
        for (const e of parsed.eventos) {
          addEvento({
            id: e.id,
            termId: state.activeTermId || null,
            tipo: e.tipo || 'Otro',
            titulo: e.titulo || 'Evento',
            fecha: e.fecha,
            horaInicio: e.horaInicio,
            horaFin: e.horaFin,
          });
          createdEventos++;
        }
        for (const c of parsed.cursadas) {
          // Map numeric days 1..7 to our 1..6; ignore Sunday (7)
          const dias = (c.dias || [])
            .map((n) => (n === 7 ? null : n))
            .filter((x) => x)
            .map((n) => n);
          addCursada({
            id: c.id,
            termId: state.activeTermId || null,
            materia: c.materia,
            dias,
            horaInicio: c.horaInicio,
            horaFin: c.horaFin,
            desde: c.desde,
            hasta: c.hasta,
          });
          createdCursadas++;
        }
        // Compute potential conflicts after import using a simulated next state
        const simulated = {
          ...state,
          eventos: [...state.eventos, ...parsed.eventos.map((e) => ({
            id: e.id, termId: state.activeTermId || null, tipo: e.tipo || 'Otro', titulo: e.titulo || 'Evento', fecha: e.fecha, horaInicio: e.horaInicio, horaFin: e.horaFin,
          }))],
          cursadas: [...state.cursadas, ...parsed.cursadas.map((c) => ({
            id: c.id, termId: state.activeTermId || null, materia: c.materia, dias: (c.dias || []).filter((n) => n && n !== 7), horaInicio: c.horaInicio, horaFin: c.horaFin, desde: c.desde, hasta: c.hasta,
          }))],
        };
        const afterConflicts = detectConflictsStatic(simulated, state.activeTermId);
        const newConflicts = Math.max(0, (afterConflicts.size || 0) - (beforeConflicts.size || 0));
        const ignored = parsed.ignored || 0;
        const msg = `Importación ICS: ${createdEventos} eventos, ${createdCursadas} cursadas, ${newConflicts} en conflicto, ${ignored} ignorados.`;
        // eslint-disable-next-line no-alert
        alert(msg);
      } catch (err) {
        // eslint-disable-next-line no-alert
        alert('Error al importar ICS');
      }
    };
    reader.readAsText(file, 'utf-8');
  }

  const materiasSugeridas = useMemo(() => {
    try {
      const data = JSON.parse(localStorage.getItem('materias') || '{}');
      return Object.values(data).map((m) => m.nombre).filter(Boolean);
    } catch { return []; }
  }, []);

  return (
    <div className="agenda-layout">
      <div className="cards">
        {/* Card: Evento rápido */}
        <EventoForm onSubmit={handleAddEvento} materiasSugeridas={materiasSugeridas} />

        {/* Card: Cursadas */}
        <div className="card">
          <div className="card-title">📘 Cursadas</div>
          <div className="actions" style={{ justifyContent: 'flex-start' }}>
            <button className="btn-primary" onClick={() => setShowCursadaModal(true)} aria-label="Nueva cursada">+ Nueva Cursada</button>
          </div>
          <div className="chips" style={{ marginTop: 8 }}>
            {state.cursadas.map((c) => (
              <div key={c.id} className="chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontWeight: 600 }}>{c.materia}</span>
                <span>{(c.dias || []).map((d) => ({ 1: 'Lu', 2: 'Ma', 3: 'Mi', 4: 'Ju', 5: 'Vi', 6: 'Sa' }[d] || d)).join('/')}</span>
                <span>{c.horaInicio}–{c.horaFin}</span>
                <button className="icon-btn" aria-label="Ver/ocultar" onClick={() => updateCursada({ ...c, visible: c.visible === false ? true : false })}>{c.visible === false ? '🙈' : '👁'}</button>
                <button className="icon-btn" aria-label="Editar" onClick={() => setShowCursadaModal(true)}>✏️</button>
                <button className="icon-btn" aria-label="Eliminar" onClick={() => removeCursada(c.id)}>🗑️</button>
              </div>
            ))}
          </div>
        </div>
        {/* Eliminamos la card de Acciones y movemos al dropdown */}
      </div>

      <div>
        {/* Dropdown Acciones (⋯) */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
          <div style={{ position: 'relative' }}>
            <button className="icon-btn" aria-label="Acciones" onClick={() => setShowActions((v) => !v)}>⋯</button>
            {showActions && (
              <div role="menu" aria-label="Acciones" style={{ position: 'absolute', right: 0, background: 'var(--surface)', border: '1px solid var(--grid)', borderRadius: 8, padding: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <button className="btn-primary" onClick={exportCSV} aria-label="Exportar CSV">Exportar CSV</button>
                <button className="btn-primary" onClick={exportICS} aria-label="Exportar ICS">Exportar ICS</button>
                <label className="btn-primary" aria-label="Importar CSV" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  Importar CSV
                  <input type="file" accept=".csv" style={{ display: 'none' }} onChange={(e) => e.target.files && e.target.files[0] && importCSV(e.target.files[0])} />
                </label>
                <label className="btn-primary" aria-label="Importar ICS" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  Importar ICS
                  <input type="file" accept=".ics,text/calendar" style={{ display: 'none' }} onChange={(e) => e.target.files && e.target.files[0] && importICS(e.target.files[0])} />
                </label>
                <button className="btn-primary" disabled title="Próximamente">Calendario Académico</button>
                <button className="btn-primary" onClick={() => { if (window.confirm('¿Borrar toda la agenda?')) clearAll(); }} aria-label="Limpiar Agenda">🧹 Limpiar Agenda</button>
              </div>
            )}
          </div>
        </div>
        <CalendarFC
          events={events}
          version={state.version}
          initialDate={initialDate}
          eventsSource={(info, success) => {
            try {
              const unicos = selectEventosUnicos(state);
              const cursadas = expandCursadasEnRango(state, info.start, info.end);
              success([...cursadas, ...unicos]);
            } catch {
              success([]);
            }
          }}
          onEventDrop={({ event }) => {
            const id = event.id;
            const isCursada = String(id).includes('-');
            if (isCursada) return; // no mover cursadas
            // actualizar evento único
            const start = event.start;
            const end = event.end || new Date(event.start.getTime() + 60 * 60 * 1000);
            removeEvento(id);
            addEvento({
              id,
              termId: state.activeTermId || null,
              tipo: 'Otro',
              titulo: event.title,
              fecha: start.toISOString().slice(0, 10),
              horaInicio: start.toISOString().slice(11, 16),
              horaFin: end.toISOString().slice(11, 16),
            });
          }}
          onEventResize={({ event }) => {
            const id = event.id;
            const isCursada = String(id).includes('-');
            if (isCursada) return;
            const start = event.start;
            const end = event.end || event.start;
            removeEvento(id);
            addEvento({
              id,
              termId: state.activeTermId || null,
              tipo: 'Otro',
              titulo: event.title,
              fecha: start.toISOString().slice(0, 10),
              horaInicio: start.toISOString().slice(11, 16),
              horaFin: end.toISOString().slice(11, 16),
            });
          }}
          initialView={view}
          onViewChange={setView}
        />
      </div>

      {showCursadaModal && (
        <CursadaForm onClose={() => setShowCursadaModal(false)} onSubmit={handleAddCursada} materiasSugeridas={materiasSugeridas} />
      )}
    </div>
  );
}


